import { ref, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * iOS 键盘处理 Hook
 * 
 * 用于解决 iOS Safari/WebView 中键盘弹出时的常见问题：
 * 1. 输入框切换时内容消失或闪烁
 * 2. 键盘遮挡输入框
 * 3. 视口高度变化导致布局错乱
 * 
 * @returns {object} 包含键盘状态和事件处理函数
 */
export function useIOSKeyboard() {
  // 键盘是否打开
  const isKeyboardOpen = ref(false)
  
  // 当前活动的输入框
  const activeInput = ref<HTMLElement | null>(null)
  
  /**
   * 处理输入框聚焦事件
   * @param event - Focus 事件对象
   */
  const handleInputFocus = async (event: FocusEvent) => {
    const target = event.target as HTMLElement
    activeInput.value = target
    isKeyboardOpen.value = true
    
    // 延迟一小段时间，等待键盘完全弹出
    await nextTick()
    setTimeout(() => {
      // 滚动输入框到可见区域，并留出一些额外空间
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center', // 将输入框定位到视口中间
          inline: 'nearest'
        })
      }
    }, 300) // iOS 键盘动画通常约为 300ms
  }
  
  /**
   * 处理输入框失焦事件
   */
  const handleInputBlur = () => {
    // 延迟重置状态，避免切换输入框时出现闪烁
    setTimeout(() => {
      // 检查是否还有其他输入框处于焦点状态
      const activeElement = document.activeElement
      if (activeElement?.tagName !== 'INPUT' && 
          activeElement?.tagName !== 'TEXTAREA') {
        isKeyboardOpen.value = false
        activeInput.value = null
      }
    }, 100)
  }
  
  /**
   * 处理 Visual Viewport 变化（iOS 键盘弹出/收起时）
   * 
   * Visual Viewport API 是检测 iOS 键盘状态的最可靠方法
   */
  const handleViewportResize = () => {
    if (window.visualViewport) {
      const viewport = window.visualViewport
      // 当视口高度小于窗口高度时，说明键盘弹出了
      const keyboardHeight = window.innerHeight - viewport.height
      
      if (keyboardHeight > 100) { // 键盘至少100px才认为是真正弹出
        isKeyboardOpen.value = true
        
        // 如果有活动的输入框，确保它在可见区域
        if (activeInput.value) {
          nextTick(() => {
            activeInput.value?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          })
        }
      } else {
        isKeyboardOpen.value = false
      }
    }
  }
  
  /**
   * 初始化键盘监听
   */
  const initKeyboardListeners = () => {
    // 添加 iOS 键盘监听
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize)
      window.visualViewport.addEventListener('scroll', handleViewportResize)
    }
    
    // 备用方案：监听窗口 resize
    window.addEventListener('resize', handleViewportResize)
  }
  
  /**
   * 移除键盘监听
   */
  const removeKeyboardListeners = () => {
    // 移除 iOS 键盘监听
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportResize)
      window.visualViewport.removeEventListener('scroll', handleViewportResize)
    }
    
    window.removeEventListener('resize', handleViewportResize)
  }
  
  // 自动初始化和清理
  onMounted(() => {
    initKeyboardListeners()
  })
  
  onUnmounted(() => {
    removeKeyboardListeners()
  })
  
  return {
    isKeyboardOpen,
    activeInput,
    handleInputFocus,
    handleInputBlur,
    handleViewportResize,
    initKeyboardListeners,
    removeKeyboardListeners
  }
}

